/**
 * Page frame for every Connect Splyt step: wordmark, content column, action
 * footer. Mobile pins the footer above the keyboard (JoinFooter); desktop puts
 * everything in one card. One layout mounts at a time, chosen by the same `sm`
 * breakpoint the join pages use in CSS, so refs and the reCAPTCHA container
 * exist exactly once.
 */
import React from 'react';
import PropTypes from 'prop-types';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';
import JoinFooter from '../join/JoinFooter';
import useKeyboardPinnedFooter from '../join/useKeyboardPinnedFooter';
import useConnectStyles from './connectStyles';

// The brand wordmark is a drawing (splyt-design-system/brand/BRAND.md), never
// retyped. These are the two files www.splyt.co uses: ink on a light
// background (its footer), live on blueberry (its hero).
export function Wordmark({ className }) {
  const classes = useConnectStyles();
  return (
    <div className={`${classes.wordmark} ${className || ''}`}>
      <img src="/brand/splyt-wordmark-ink.svg" alt="Splyt" height={40} />
    </div>
  );
}

Wordmark.propTypes = {
  className: PropTypes.string,
};

Wordmark.defaultProps = {
  className: '',
};

export function useIsDesktop() {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });
}

export default function ConnectShell({ children, footer }) {
  const classes = useConnectStyles();
  const isDesktop = useIsDesktop();
  const { footerRef, footerHeight, pinned } = useKeyboardPinnedFooter();

  if (isDesktop) {
    return (
      <div className={classes.root}>
        <div className={classes.desktopWrapper}>
          <div className={classes.desktopHeader}>
            <Wordmark />
          </div>
          <div className={classes.desktopCard}>
            <div className={classes.desktopContent}>{children}</div>
            {footer && <div className={classes.desktopFooter}>{footer}</div>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.mobileWrapper}>
        <div className={classes.mobileHeader}>
          <Wordmark />
        </div>
        <div className={classes.content} style={{ paddingBottom: footerHeight }}>
          {children}
        </div>
        {footer && (
          <JoinFooter footerRef={footerRef} pinned={pinned}>
            {footer}
          </JoinFooter>
        )}
      </div>
    </div>
  );
}

ConnectShell.propTypes = {
  children: PropTypes.node,
  footer: PropTypes.node,
};

ConnectShell.defaultProps = {
  children: null,
  footer: null,
};

/** Centered spinner or message, for the loading and expired states. */
export function ConnectState({ title, text }) {
  const classes = useConnectStyles();
  return (
    <div className={classes.root}>
      <div className={classes.stateWrap}>
        <Wordmark className={classes.stateWordmark} />
        {title ? (
          <>
            <h1 className={classes.stateTitle}>{title}</h1>
            {text && <p className={classes.stateText}>{text}</p>}
          </>
        ) : (
          <div className={classes.spinner} role="status" aria-label="Loading" />
        )}
      </div>
    </div>
  );
}

ConnectState.propTypes = {
  title: PropTypes.string,
  text: PropTypes.string,
};

ConnectState.defaultProps = {
  title: '',
  text: '',
};
